import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { FormGroup, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { User } from '@models/user.model';
import { AuthService } from '@services';
import { clearField } from '@shared/utils/form-field.utils';
import { getError } from '@shared/utils/error.utils';
import { objectIsEmpty } from '@shared/utils/helpers.util';
import { RangeTableFilter, TableFilter, TableFilterConfig, TableFilterType } from '@shared/components/table-filter-menu/table-filter-menu.models';
import { Unsubscriber } from '@shared/classes/unsubscriber';
import { LocalStorageService } from 'ngx-webstorage';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-table-filter-menu',
  templateUrl: './table-filter-menu.component.html',
  styleUrls: ['./table-filter-menu.component.scss'],
})
export class TableFilterMenuComponent extends Unsubscriber implements OnInit {
  protected readonly TableFilterType = TableFilterType;
  readonly clearField = clearField;
  changedControls: Record<string, number> = {};
  user: User = this.authService.user;

  getError = getError;

  form: UntypedFormGroup;
  initialForm: any;
  isLoading: boolean;
  isDirty: boolean;

  @Input() filtered$: Observable<boolean>;
  @Output() filtersApplied: EventEmitter<any> = new EventEmitter<any>();
  @Output() filtersCleared: EventEmitter<void> = new EventEmitter<void>();
  private _filtersFromStorage: Record<string, any> = {};

  constructor(
    private fb: UntypedFormBuilder,
    private authService: AuthService,
    private storage: LocalStorageService,
    private dialogRef: MatDialogRef<TableFilterMenuComponent>,
    @Inject(MAT_DIALOG_DATA) public config: TableFilterConfig,
  ) {
    super();
  }

  get totalCounter(): number {
    return Object.values(this.changedControls).reduce((acc, value) => {
      return acc + value;
    }, 0);
  }

  get filters(): Record<string, any> {
    return this._filtersFromStorage;
  }

  set filters(filters: Record<string, any>) {
    this._filtersFromStorage = filters;
  }

  private get storageKey(): string {
    return `${this.authService.user.userLogin}_${this.config.storageKey}`;
  }

  getSubGroup(groupControlName: string, filterControlName: string): UntypedFormGroup {
    return this.getGroup(groupControlName).get(filterControlName) as UntypedFormGroup;
  }

  displayFilter(groupName: string, filter: TableFilter): boolean {
    const formGroup = this.getGroup(groupName);
    const isFilterDisplayed = filter?.displayCondition ? filter.displayCondition(formGroup) : true;
    return isFilterDisplayed && !filter?.onlyHotFilter;
  }

  getGroup(groupName: string): UntypedFormGroup {
    return this.form.get(groupName) as UntypedFormGroup;
  }

  clearGroup(groupName: string): void {
    this.filters = {
      ...this.form.getRawValue(),
      [groupName]: this.initialForm[groupName],
    };
    this.form.patchValue(this.filters);
  }

  ngOnInit(): void {
    this.initForm();

    this.setChangedControlsSubscription();

    this.patchForm();
  }

  clearFilters(): void {
    this.isLoading = true;
    this.storage.clear(this.storageKey);
    this.filtersCleared.emit();
    this.form.reset(this.initialForm);
    this.isLoading = false;
    this.dialogRef.close();
  }

  applyFilterForm(): void {
    this.isLoading = true;
    const currentFilters = this.form.getRawValue();
    this.saveFiltersToStorage(currentFilters);
    this.filtersApplied.emit(currentFilters);
    this.dialogRef.close();
  }

  saveFiltersToStorage(filters: any): void {
    this.storage.store(
      this.storageKey,
      objectIsEmpty(filters) ? {} : filters,
    );
    this.retrieveFiltersFromStorage();
  }

  private getChangedControlsAmount(formGroupName: string, currentValue: any): number {
    const initialForm = this.initialForm[formGroupName];
    return this.countChangedControls(initialForm, currentValue, formGroupName);
  }

  private countChangedControls(initialForm: any, currentForm: any, formGroupName: string = ''): number {
    return Object.entries(currentForm).reduce((acc, [name, value]) => {
      const isOnlyHotFilterDisplayed = this.findFilterConfig(formGroupName, name)?.onlyHotFilter;

      if (isOnlyHotFilterDisplayed) {
        return acc;
      }
      if (initialForm[name] === undefined) {
        return 1;
      }
      if (typeof value === 'object' && value !== null && !(value instanceof Date)) {
        return acc + this.countChangedControls(initialForm[name], value);
      }
      return acc + +(JSON.stringify(initialForm[name]) !== JSON.stringify(value) && value !== null);
    }, 0);
  }

  private findFilterConfig(groupName: string, controlName: string): TableFilter {
    return this.config.filterGroups.find(group => group.controlName === groupName)
      ?.filters.find(filter => filter.controlName === controlName);
  }

  private retrieveFiltersFromStorage(): Record<string, any> {
    this.filters = this.storage.retrieve(this.storageKey) ?? {};
    return this.filters;
  }

  private initForm(): void {
    this.form = this.fb.group(
      this.config.filterGroups.reduce((acc, group) => {
        if (!group.controlName) {
          return acc;
        }
        acc[group.controlName] = this.createGroup(group.filters);
        return acc;
      }, {}),
    );
    this.saveInitialForm();
  }

  private createGroup(filters: TableFilter[]): FormGroup {
    const groupConfig = filters.reduce((acc, filter) => {
      if (filter.type === TableFilterType.DoubleDate || filter.type === TableFilterType.DoubleNumberInput) {
        acc[filter.controlName] = this.createDoubleInputGroup(filter);
      } else {
        acc[filter.controlName] = [filter.initialValue, filter.validators];
      }
      return acc;
    }, {});

    return this.fb.group(groupConfig);
  }

  private createDoubleInputGroup(filter: TableFilter): FormGroup {
    const fromFilter = (filter as RangeTableFilter).fromFilter;
    const toFilter = (filter as RangeTableFilter).toFilter;

    return this.fb.group({
      [fromFilter.controlName]: [fromFilter.initialValue, fromFilter.validators],
      [toFilter.controlName]: [toFilter.initialValue, toFilter.validators],
    }, { validators: filter.validators });
  }

  private saveInitialForm(): void {
    this.initialForm = this.form.getRawValue();
  }

  private patchForm(): void {
    this.form.patchValue(this.retrieveFiltersFromStorage());
  }

  private setChangedControlsSubscription(): void {
    this.subscriptions = this.form.valueChanges.subscribe((v) => {
      this.config.filterGroups.forEach((g) => {
        this.changedControls[g.controlName] = this.getChangedControlsAmount(g.controlName, v[g.controlName]);
      });
      this.isDirty = Object.values(this.changedControls).find((v) => v > 0) > 0;
    });
  }
}
